import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus } from "lucide-react";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";
import { BrandApi } from "@/api/brand.api";
import { RedemptionMerchantApi } from "@/api/redemptionMerchant.api";
import { PlatformCategoryApi } from "@/api/platformCategory.api";
import { toast } from "sonner";
import { APIClient, HandleError } from "@/api/axiosClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link, useNavigate } from "react-router-dom";

interface MerchandiseFormFields {
  productCode?: string;
  productName?: string;
  productPrice?: string;
}

// Attribute type
interface Attribute {
  attributeName: string;
  attributeValues: { attributeValue: string }[];
}

const AttributeDialog = ({
  open,
  onOpenChange,
  attributes,
  setAttributes,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attributes: Attribute[];
  setAttributes: (attrs: Attribute[]) => void;
}) => {
  React.useEffect(() => {
    if (open && attributes.length === 0) {
      setAttributes([
        { attributeName: "", attributeValues: [{ attributeValue: "" }] },
      ]);
    }
  }, [open]);

  console.log(attributes, "attributes----");

  const handleAttrNameChange = (idx: number, value: string) => {
    const updated = [...attributes];
    updated[idx].attributeName = value;
    setAttributes(updated);
  };

  const handleValueChange = (
    attrIdx: number,
    valIdx: number,
    value: string
  ) => {
    const updated = [...attributes];
    updated[attrIdx].attributeValues[valIdx].attributeValue = value;
    setAttributes(updated);
  };

  const handleAddValue = (attrIdx: number) => {
    const updated = [...attributes];
    updated[attrIdx].attributeValues.push({ attributeValue: "" });
    setAttributes(updated);
  };

  const handleDeleteValue = (attrIdx: number, valIdx: number) => {
    const updated = [...attributes];
    updated[attrIdx].attributeValues.splice(valIdx, 1);
    if (updated[attrIdx].attributeValues.length === 0) {
      updated[attrIdx].attributeValues.push({ attributeValue: "" });
    }
    setAttributes(updated);
  };

  const handleAddAttribute = () => {
    setAttributes([
      ...attributes,
      { attributeName: "", attributeValues: [{ attributeValue: "" }] },
    ]);
  };

  const handleDeleteAttribute = (idx: number) => {
    const updated = [...attributes];
    updated.splice(idx, 1);
    setAttributes(
      updated.length
        ? updated
        : [{ attributeName: "", attributeValues: [{ attributeValue: "" }] }]
    );
  };

  const handleSaveAll = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl w-full p-6 max-h-[80vh] flex flex-col">
        <h2 className="text-lg font-semibold mb-4">Product Attributes</h2>
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {attributes.map((attr, idx) => (
            <div key={idx} className="bg-gray-100 rounded-xl p-4">
              <div className="flex flex-col md:flex-row gap-4 mb-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">
                    Attribute Name
                  </label>
                  <Input
                    value={attr.attributeName}
                    onChange={(e) => handleAttrNameChange(idx, e.target.value)}
                    placeholder="Attribute Name"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">
                    Values
                  </label>
                  {attr.attributeValues.map((val, vIdx) => (
                    <div key={vIdx} className="flex items-center gap-2 mb-1">
                      <Input
                        value={val.attributeValue}
                        onChange={(e) =>
                          handleValueChange(idx, vIdx, e.target.value)
                        }
                        placeholder={`Value ${vIdx + 1}`}
                      />
                      {attr.attributeValues.length > 1 && (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteValue(idx, vIdx)}
                        >
                          -
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="link"
                    className="p-0 h-auto min-w-0 text-xs mt-1"
                    onClick={() => handleAddValue(idx)}
                  >
                    + Add Value
                  </Button>
                </div>
              </div>
              <div className="flex justify-between items-center mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteAttribute(idx)}
                >
                  Delete Attribute
                </Button>
                <Button type="button" size="sm" onClick={() => {}}>
                  Save
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between mt-6 flex-wrap gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            className="flex items-center gap-2"
            onClick={handleAddAttribute}
          >
            <Plus className="h-4 w-4" /> Add Attribute
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveAll}>
              Save All
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CURRENCY_OPTIONS = [
  {
    iso3CurrencyCode: "USD",
    currencyName: "United States Dollar",
    currencySymbol: "$",
    id: 1,
  },
  { iso3CurrencyCode: "EUR", currencyName: "Euro", currencySymbol: "€", id: 2 },
  {
    iso3CurrencyCode: "GBP",
    currencyName: "British Pound",
    currencySymbol: "£",
    id: 3,
  },
  {
    iso3CurrencyCode: "INR",
    currencyName: "Indian Rupee",
    currencySymbol: "₹",
    id: 4,
  },
  {
    iso3CurrencyCode: "AED",
    currencyName: "United Arab Emirates Dirham",
    currencySymbol: "د.إ",
    id: 5,
  },
  {
    iso3CurrencyCode: "ETB",
    currencyName: "Ethiopian Birr",
    currencySymbol: "Br",
    id: 6,
  },
];

const MerchandiseProductCreatePage = () => {
  const navigate = useNavigate();
  const [attrDialogOpen, setAttrDialogOpen] = useState(false);
  const [productImage, setProductImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [merchants, setMerchants] = useState([]);
  const [merchantsLoading, setMerchantsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedFulfilmentType, setSelectedFulfilmentType] = useState("");
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [formD, setFormD] = useState<MerchandiseFormFields>({});
  const [productDesc, setProductDesc] = useState("");
  const [productDetails, setProductDetails] = useState("");
  const [htmlText, setHtmlText] = useState("");
  const [howToUse, setHowToUse] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [loyyalCommissionPercentage, setLoyyalCommissionPercentage] =
    useState("");
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<number[]>([]);

  useEffect(() => {
    async function fetchBrands() {
      setBrandsLoading(true);
      try {
        const { data } = await BrandApi.fetchAllBrands();
        setBrands(data || []);
      } catch (err) {
        setBrands([]);
        toast.error("Failed to fetch brands");
      } finally {
        setBrandsLoading(false);
      }
    }
    fetchBrands();
  }, []);

  useEffect(() => {
    async function fetchMerchants() {
      setMerchantsLoading(true);
      try {
        const { data } =
          await RedemptionMerchantApi.fetchAllRedemptionMerchants();
        setMerchants(data?.redemptionMerchantVOS || []);
      } catch (err) {
        setMerchants([]);
        toast.error("Failed to fetch merchants");
      } finally {
        setMerchantsLoading(false);
      }
    }
    fetchMerchants();
  }, []);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const { data } = await PlatformCategoryApi.fetchCategoryTree();
      setCategories(data?.subCategoryVOs || []);
    } catch (err) {
      setCategories([]);
      toast.error("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormD((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    let updatedFormData = {};
    try {
      updatedFormData = {
        brandVO: { id: selectedBrand },
        redemptionMerchantVO: { id: selectedMerchant },
        productCode: formD.productCode,
        productName: formD.productName,
        productDescription: productDesc,
        productDetails: productDetails,
        productType: "MERCHANDISE",
        termsAndConditions: htmlText,
        howToUse: howToUse,
        productPrice: formD.productPrice,
        currencyVO: selectedCurrency ? { id: selectedCurrency.id } : null,
        loyyalCommissionPercentage: loyyalCommissionPercentage,
        fulfilmentType: selectedFulfilmentType,
        platformCategoryIds: selectedCategory ? [selectedCategory.id] : [],
        productAttributeVOs: attributes,
      };
      const formData = new FormData();
      if (productImage) {
        formData.append("giftCardProductLogo", productImage);
      }
      const blob = new Blob([JSON.stringify(updatedFormData)], {
        type: "application/json",
      });
      formData.append("giftCardProductVO", blob);
      await APIClient("/product/createOrUpdateGiftCardProduct", formData, {
        requestType: "post",
        contentType: "multipart/form-data",
      });
      toast.success("Merchandise added successfully");
      // Navigate back to merchandise products screen after successful creation
      navigate("/rewardpoint/products/merchandise");
      setFormD({});
      setProductDesc("");
      setProductDetails("");
      setHtmlText("");
      setHowToUse("");
      setSelectedBrand(null);
      setSelectedMerchant(null);
      setSelectedCategory(null);
      setSelectedFulfilmentType("");
      setProductImage(null);
      setImagePreview(null);
      setAttributes([]);
    } catch (error) {
      toast.error(HandleError(error));
    } finally {
      setLoading(false);
    }
  };

  const renderCategories = (categories) => (
    <div className="flex flex-col gap-2">
      {categories.map((cat) => {
        const isExpanded = expandedCategoryIds.includes(cat.id);
        const hasSub = cat.subCategoryVOs && cat.subCategoryVOs.length > 0;
        return (
          <div key={cat.id} className="ml-2">
            <div
              className="flex items-center gap-2 border rounded-lg px-4 py-2 cursor-pointer hover:bg-gray-50"
              onClick={() => {
                if (hasSub) {
                  setExpandedCategoryIds((ids) =>
                    isExpanded
                      ? ids.filter((id) => id !== cat.id)
                      : [...ids, cat.id]
                  );
                } else {
                  setSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }
              }}
            >
              <span className="flex-1">{cat.categoryName}</span>
              {hasSub && <span>{isExpanded ? "▼" : "▶"}</span>}
              <input
                type="radio"
                checked={selectedCategory?.id === cat.id}
                onChange={() => {
                  setSelectedCategory(cat);
                  setCategoryDialogOpen(false);
                }}
                onClick={(e) => e.stopPropagation()}
                readOnly
              />
            </div>
            {hasSub && isExpanded && renderCategories(cat.subCategoryVOs)}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6 w-full py-8 px-8">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" asChild className="bg-white">
          <Link to="/rewardpoint/products/merchandise">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Create Merchandise Product
          </h1>
          <p className="text-gray-500">
            Add a new merchandise product to your catalog
          </p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-8">
            {/* General Information */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  General Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Product Image */}
                  <div>
                    <label className="block font-medium mb-2">
                      Product Image
                    </label>
                    <div className="w-28 h-28 rounded-lg bg-gray-100 flex items-center justify-center mb-2 relative">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        id="merch-image-upload"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setProductImage(file);
                            setImagePreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                      <label htmlFor="merch-image-upload">
                        <Button variant="ghost" size="icon" asChild>
                          <span>
                            <Plus className="h-8 w-8 text-gray-400" />
                          </span>
                        </Button>
                      </label>
                      {imagePreview && (
                        <img
                          src={imagePreview}
                          alt="Product Preview"
                          className="absolute inset-0 w-24 h-24 object-contain rounded"
                        />
                      )}
                    </div>
                  </div>
                  {/* Brand & Merchant */}
                  <div>
                    <label className="block font-medium mb-1">Brand*</label>
                    <Select
                      value={selectedBrand}
                      onValueChange={setSelectedBrand}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {brandsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : brands.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No brands
                          </div>
                        ) : (
                          brands.map((b) => (
                            <SelectItem key={b.id} value={b.id + ""}>
                              {b.brandName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Merchant*</label>
                    <Select
                      value={selectedMerchant}
                      onValueChange={setSelectedMerchant}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Merchant" />
                      </SelectTrigger>
                      <SelectContent>
                        {merchantsLoading ? (
                          <div className="px-3 py-2 text-gray-500">
                            Loading...
                          </div>
                        ) : merchants.length === 0 ? (
                          <div className="px-3 py-2 text-gray-500">
                            No merchants
                          </div>
                        ) : (
                          merchants.map((m) => (
                            <SelectItem key={m.id} value={m.id + ""}>
                              {m.merchantName}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Product Name & Code */}
                  <div>
                    <label className="block font-medium mb-1">
                      Product Name*
                    </label>
                    <Input
                      name="productName"
                      value={formD.productName || ""}
                      onChange={handleInputChange}
                      placeholder="Enter product name"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Product Code*
                    </label>
                    <Input
                      name="productCode"
                      value={formD.productCode || ""}
                      onChange={handleInputChange}
                      placeholder="Enter product code.."
                    />
                  </div>
                  {/* Product Description */}
                  <div className="md:col-span-2">
                    <label className="block font-medium mb-1">
                      Product Description
                    </label>
                    <Textarea
                      value={productDesc}
                      onChange={(e) => setProductDesc(e.target.value)}
                      placeholder="Enter product description"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Merchandise Details */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Merchandise Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Platform Category */}
                  <div>
                    <label className="block font-medium mb-1">
                      Platform Category
                    </label>
                    <Dialog
                      open={categoryDialogOpen}
                      onOpenChange={setCategoryDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full text-left"
                          onClick={fetchCategories}
                        >
                          {selectedCategory
                            ? selectedCategory.categoryName
                            : "Select"}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-md w-full p-6">
                        <div className="rounded-xl border p-6 bg-white">
                          {categoriesLoading ? (
                            <div className="text-center text-gray-500 py-8">
                              Loading...
                            </div>
                          ) : (
                            renderCategories(categories)
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Attributes, product details, etc. */}
                <div>
                  <label className="block font-medium mb-2">
                    Manage Attributes
                  </label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setAttrDialogOpen(true)}
                  >
                    Manage Attributes
                  </Button>

                  {/* Display current attributes */}
                  {attributes.length > 0 &&
                    attributes.some((attr) => attr.attributeName) && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Current Attributes:
                        </h4>
                        <div className="space-y-2">
                          {attributes
                            .filter((attr) => attr.attributeName)
                            .map((attr, idx) => (
                              <div
                                key={idx}
                                className="bg-gray-50 rounded-lg p-3 border"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-800">
                                      {attr.attributeName}:
                                    </span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {attr.attributeValues
                                        .filter((val) => val.attributeValue)
                                        .map((val, valIdx) => (
                                          <span
                                            key={valIdx}
                                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                          >
                                            {val.attributeValue}
                                          </span>
                                        ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                </div>
                <div>
                  <label className="block font-medium mb-2">
                    Product Details
                  </label>
                  <Textarea
                    value={productDetails}
                    onChange={(e) => setProductDetails(e.target.value)}
                    placeholder="Enter product details"
                  />
                </div>

                {/* Merchant-side order placement Mechanism */}
                <div>
                  <label className="block font-medium mb-1">
                    Merchant-side order placement Mechanism
                  </label>
                  <Select
                    value={selectedFulfilmentType}
                    onValueChange={setSelectedFulfilmentType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Fulfilment Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MERCHANT_API">MERCHANT_API</SelectItem>
                      <SelectItem value="MERCHANT_EMAIL">
                        MERCHANT_EMAIL
                      </SelectItem>
                      <SelectItem value="LOYYAL_GENERATED">
                        LOYYAL_GENERATED
                      </SelectItem>
                      <SelectItem value="MERCHANT_UPLOADED">
                        MERCHANT_UPLOADED
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="space-y-8">
            {/* Pricing & Terms */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  Pricing & Terms
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block font-medium mb-1">
                      Product Price
                    </label>
                    <Input
                      name="productPrice"
                      value={formD.productPrice || ""}
                      onChange={handleInputChange}
                      placeholder="Enter price"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Currency*</label>
                    <Select
                      value={selectedCurrency?.id || ""}
                      onValueChange={(v) =>
                        setSelectedCurrency(
                          CURRENCY_OPTIONS.find((c) => c.id === Number(v))
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select Currency" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.id} value={opt.id.toString()}>
                            {opt.currencyName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Loyyal Commission* (%)
                    </label>
                    <Input
                      name="loyyalCommissionPercentage"
                      value={loyyalCommissionPercentage}
                      onChange={(e) =>
                        setLoyyalCommissionPercentage(e.target.value)
                      }
                      placeholder="Commission"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">
                      Terms & Conditions
                    </label>
                    <Textarea
                      value={htmlText}
                      onChange={(e) => setHtmlText(e.target.value)}
                      placeholder="Enter terms & conditions"
                      rows={4}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* How To Use */}
            <Card className="border-0 shadow-sm overflow-hidden bg-white w-full">
              <CardHeader className="card-header-compact bg-gray-50">
                <CardTitle className="text-lg font-medium text-gray-800">
                  How To Use
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <Textarea
                  value={howToUse}
                  onChange={(e) => setHowToUse(e.target.value)}
                  placeholder="Enter how to use"
                  rows={6}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        <Separator className="my-8" />
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => window.history.back()}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Create Product"}
          </Button>
        </div>
      </form>

      {/* Attribute Dialog */}
      <AttributeDialog
        open={attrDialogOpen}
        onOpenChange={setAttrDialogOpen}
        attributes={attributes}
        setAttributes={setAttributes}
      />
    </div>
  );
};

export default MerchandiseProductCreatePage;
